import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import { getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem } from '../../../Helper/cookieHelper'
import { request } from '../../../Redux/Sagas/requests/api'
import moment from 'moment'
import { checkIsAdminOrManager } from '../../../helper'
import { primary_color } from '../../../Helper/uiHelper'
import { filterActiveEditUnits } from '../../../Helper/publicPageHelper'

export const EDIT_MOVE_CONTEXT_KEY = 'edit_move_context'
export const EDIT_BOOKING_FOCUS_MESSAGE = 'edit_booking_focus'

const EditPurchase = () => {
  const [showIframe, setShowIframe] = useState(false)
  const [pageUUID, setPageUUID] = useState()
  const [isPastAcademicYear, setIsPastAcademicYear] = useState(false)
  const [focusedPupilIndex, setFocusedPupilIndex] = useState(0)
  const [savedAcademicYear, setSavedAcademicYear] = useState('')
  const [savedStudentYearGroup, setSavedStudentYearGroup] = useState('')
  const [savedBlockName, setSavedBlockName] = useState('')
  const [savedLockerNumber, setSavedLockerNumber] = useState('')
  const [prefillAcademicYearId, setPrefillAcademicYearId] = useState(null)

  const currentRow = useSelector((state) => state.CMS.currentRow)
  const navigation = useSelector((state) => state.Navigations)
  const user = useSelector((state) => state.User.user)

  let domain_id, payment_id
  if (currentRow?.[0]) {
    domain_id = currentRow?.[0]?.domain_id
    payment_id = currentRow?.[0]?.id
  }

  const isAdmin = checkIsAdminOrManager(user?.role)

  const applySummaryFromUnit = (unit, index = 0, moveContext = null) => {
    if (!unit && !moveContext) return
    const yearId = moveContext?.item_date_time_id || unit?.item_date_time_id
    const yearLabel = moveContext?.item_date_time_id_label || unit?.item_date_time_id_label || ''
    setPrefillAcademicYearId(yearId || null)
    setFocusedPupilIndex(index)
    setSavedAcademicYear(yearLabel)
    setSavedStudentYearGroup(moveContext?.category_id_label || unit?.category_id_label || '')
    setSavedBlockName(moveContext?.location_id_label || unit?.location_id_label || '')
    setSavedLockerNumber(moveContext?.item_id_label || unit?.item_id_label || '')
  }

  useEffect(() => {
    let storage_timer = getLocalStorageItem('timer') && JSON.parse(getLocalStorageItem('timer'))
    let current_storage_timer = storage_timer?.[parseInt(domain_id)]
    let bookingPage = navigation?.HeaderNav?.find((item) => item?.route === 'book-my-locker-list')
    setIsPastAcademicYear(false)

    let moveContext = null
    try {
      const raw = sessionStorage.getItem(EDIT_MOVE_CONTEXT_KEY)
      if (raw) {
        moveContext = JSON.parse(raw)
        if (String(moveContext?.payment_id) !== String(payment_id)) {
          moveContext = null
        }
      }
    } catch (e) {
      moveContext = null
    }

    request('get-booking-by-payment-id', 'POST', {
      payment_id,
      // Closed academic years must still show for admin edit / move handoff.
      show_off_display: true,
    }).then((res) => {
      const units = filterActiveEditUnits(res?.data?.units)
      const first = units?.[0]
      if (first || moveContext) {
        applySummaryFromUnit(first, 0, moveContext)
      }
      const formattedDate = moment(units?.[0]?.post_time || res?.data?.post_time, [
        'D MMM, YYYY',
        'DD MMM, YYYY',
        'YYYY-MM-DD',
      ], true)

      // Past-year gate for non-admin only; admin must be able to edit closed years.
      if (!isAdmin && formattedDate.isValid() && !formattedDate.isAfter(moment(), 'day')) {
        setIsPastAcademicYear(true)
      } else {
        setIsPastAcademicYear(false)
      }
    })
    request(`get-page/${bookingPage?.page_uuid}`).then((res) => {
      let id = res?.data?.child_pages?.[0]?.uuid
      setPageUUID(id)

      if (current_storage_timer?.session_uuid) {
        request(`expire-reservation-session/${current_storage_timer?.session_uuid}`, 'GET').then((res) => {
          createReservation()
        })
      } else {
        createReservation()
      }
    })

    return () => {
      let storage_timer = getLocalStorageItem('timer') && JSON.parse(getLocalStorageItem('timer'))
      let current_storage_timer = storage_timer?.[parseInt(domain_id)]
      request(`expire-reservation-session/${current_storage_timer?.session_uuid}`, 'GET')
      removeLocalStorageItem('timer')
    }
  }, [])

  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return
      const data = event.data
      if (!data || data.type !== EDIT_BOOKING_FOCUS_MESSAGE) return
      const nextYear = data.item_date_time_id_label
      const nextGroup = data.category_id_label
      const nextBlock = data.location_id_label
      const nextLocker = data.item_id_label
      // Ignore empty focus payloads so late iframe posts cannot wipe the sticky summary.
      if (!nextYear && !nextGroup && !nextBlock && !nextLocker) {
        if (typeof data.index === 'number') setFocusedPupilIndex(data.index)
        return
      }
      setFocusedPupilIndex(typeof data.index === 'number' ? data.index : 0)
      if (nextYear) setSavedAcademicYear(nextYear)
      if (nextGroup) setSavedStudentYearGroup(nextGroup)
      if (nextBlock) setSavedBlockName(nextBlock)
      if (nextLocker) setSavedLockerNumber(nextLocker)
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  const createReservation = (forDomainId) => {
    const reservationDomainId = forDomainId || domain_id
    let data = {}
    const new_session_id = 'BOOKING-SESSION-' + uuidv4()
    let payload = {
      session_key: new_session_id,
      reservation_widget: 12,
      domain_id: reservationDomainId,
      payment_id,
    }
    let current_row_timer = {
      session_id: new_session_id,
    }
    request('create-reservation', 'POST', payload).then((res) => {
      current_row_timer = {
        ...current_row_timer,
        time: new Date().toString(),
        session_uuid: res?.data?.data?.uuid,
        duration: 1800,
      }
      data[reservationDomainId] = current_row_timer
      setLocalStorageItem('timer', JSON.stringify(data))
      setShowIframe(true)
    })
  }

  let link = `${window.location.origin}/book-my-locker-list?parent_id=${domain_id}&page_id=${pageUUID}&isShowOnlyContent=true&payment_id=${payment_id}&edit_booking=true`
  if (isAdmin) {
    link = `${window.location.origin}/book-my-locker-list?parent_id=${domain_id}&page_id=${pageUUID}&isShowOnlyContent=true&payment_id=${payment_id}&edit_booking=true&is_admin=true&show_off_display=true`
  }
  if (prefillAcademicYearId) {
    link += `&item_date_time_id=${prefillAcademicYearId}`
  }
  if (!showIframe) return

  const savedBookingSummary = (
    <div
      className='d-flex flex-wrap mb-3'
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        background: '#fff',
        paddingTop: '0.5rem',
        paddingBottom: '0.5rem',
        borderBottom: '1px solid #eee',
      }}>
      <div className='mr-5 mb-2'>
        <div className='subtitle1 font-weight-bold' style={{ color: primary_color }}>
          Pupil / Locker
        </div>
        <div className='subtitle1 text-muted' style={{ fontWeight: 100 }}>
          {focusedPupilIndex + 1}
        </div>
      </div>
      <div className='mr-5 mb-2'>
        <div className='subtitle1 font-weight-bold' style={{ color: primary_color }}>
          Academic year
        </div>
        <div className='subtitle1 text-muted' style={{ fontWeight: 100 }}>
          {savedAcademicYear || '—'}
        </div>
      </div>
      <div className='mr-5 mb-2'>
        <div className='subtitle1 font-weight-bold' style={{ color: primary_color }}>
          Student Year Group
        </div>
        <div className='subtitle1 text-muted' style={{ fontWeight: 100 }}>
          {savedStudentYearGroup || '—'}
        </div>
      </div>
      <div className='mr-5 mb-2'>
        <div className='subtitle1 font-weight-bold' style={{ color: primary_color }}>
          Block
        </div>
        <div className='subtitle1 text-muted' style={{ fontWeight: 100 }}>
          {savedBlockName || '—'}
        </div>
      </div>
      <div className='mr-5 mb-2'>
        <div className='subtitle1 font-weight-bold' style={{ color: primary_color }}>
          Locker number
        </div>
        <div className='subtitle1 text-muted' style={{ fontWeight: 100 }}>
          {savedLockerNumber || '—'}
        </div>
      </div>
    </div>
  )

  if (isPastAcademicYear) {
    return (
      <div className='d-flex flex-column justify-content-center align-items-center'>
        <div className='align-self-start w-100 px-1'>{savedBookingSummary}</div>
        <div>
          <div>
            <svg width='169' height='168' viewBox='0 0 169 168' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M166.609 137.671C171.993 147.003 165.235 158.667 154.483 158.667H14.5158C3.74254 158.667 -2.98388 146.985 2.38921 137.671L72.3735 16.3296C77.7597 6.99365 91.2501 7.01057 96.6267 16.3296L166.609 137.671ZM84.5001 112.584C77.0903 112.584 71.0834 118.591 71.0834 126.001C71.0834 133.41 77.0903 139.417 84.5001 139.417C91.9099 139.417 97.9168 133.41 97.9168 126.001C97.9168 118.591 91.9099 112.584 84.5001 112.584ZM71.7621 64.3581L73.9257 104.025C74.0269 105.881 75.5617 107.334 77.4205 107.334H91.5797C93.4385 107.334 94.9733 105.881 95.0745 104.025L97.238 64.3581C97.3474 62.3532 95.7511 60.6673 93.7433 60.6673H75.2566C73.2488 60.6673 71.6528 62.3532 71.7621 64.3581Z'
                fill='#EAEAEA'
              />
            </svg>
          </div>
        </div>
        <div className='body2'>You can't edit the booking for past academic year</div>
      </div>
    )
  }
  return (
    <div className='p-5' style={{ marginLeft: '-2rem' }}>
      {savedBookingSummary}
      <iframe style={{ height: '80vh', width: '100%', border: 'none' }} src={link} title='description'></iframe>
    </div>
  )
}

export default EditPurchase
